import {
  PHASES,
  leggibile,
  PLANT_GROUP_TEXT,
  type DocumentBlockDto,
  type PhaseCommonDto,
  type PhaseEightDto,
  type PhaseElevenDto,
  type PhaseFigureDto,
  type PhaseFiveDto,
  type PhaseFourDto,
  type PhaseNineDto,
  type PhaseOneDto,
  type PhaseSevenDto,
  type PhaseSixDto,
  type PhaseTenDto,
  type PhaseThreeDto,
  type PhaseTwoDto,
  type PhaseZeroDto,
  type ProjectDocumentDto,
} from '@bk/contracts';
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { errorMessage } from '../../lib/api';
import { useTenantQuery } from '../../lib/useTenant';
import { Fase6Body } from './Fase6';
import { Fase7Body } from './Fase7';
import { Diagnosis } from './Fase10';
import { Proposals, ProposalsSummary } from './Fase11';
import { clock, spent } from './Sinapsi';
import './chiusura.css';
import './progetto.css';

/**
 * Il documento scaricabile (contratto n. 1, art. 3.17 e 4.2, versione 2.13; ADR 0179): una pagina navigabile nello stile
 * del percorso, con l'indice delle fasi sempre visibile e la fase corrente evidenziata; ogni fase con il suo livello e le
 * sue voci in schede e tabelle; il chip della fonte apre il «perché?» in un pannello laterale; in testa la nota di tutela,
 * in fondo le fonti, «Cosa ci ha detto il titolare», le scelte di Timonio e le domande aperte. Si salva e si stampa in PDF
 * con la stessa impaginazione (foglio di stile chiaro, ogni fase da pagina nuova). Ogni dato viene dalle pagine delle fasi.
 */

/** Un «perché?»: la fonte o il ragionamento dietro un'affermazione, aperto nel pannello laterale. */
interface Why {
  titolo: string;
  fonte: string | null;
  grado: string | null;
  testo: string | null;
}
const WhyCtx = createContext<(w: Why) => void>(() => undefined);

/** Il chip della fonte: la fonte per nome e il grado; un clic apre il «perché?». */
function Src({ fonte, grado, titolo, testo }: { fonte: string | null | undefined; grado?: string | null; titolo: string; testo?: string | null }) {
  const open = useContext(WhyCtx);
  if (!fonte && !testo) return null;
  const g = grado ?? (/·\s*([ABCDT])\s*$/.exec(fonte ?? '')?.[1] ?? null);
  // Il grado scritto in fondo al nome («visura · A») si mostra una volta sola, nel suo tondo.
  const name = (fonte ?? '').replace(/\s*·\s*[ABCDT]\s*$/, '');
  return (
    <button type="button" className={`pg-src g-${(g ?? 'x').toLowerCase()}`} onClick={() => open({ titolo, fonte: fonte ?? null, grado: g, testo: testo ?? null })} title={`Perché? ${fonte ?? ''}`}>
      <span>{name || 'perché?'}</span>
      {g && <b>{g}</b>}
    </button>
  );
}

/** Il grado di una conclusione (art. 3.37): certo, probabile, possibile. */
function Grade({ g }: { g: string | null | undefined }) {
  if (!g) return null;
  return <span className={`pg-grade ${g}`}>{g}</span>;
}

/** Il livello di una fase in cinque barrette (art. 9.3). */
function Bars({ v }: { v: number | null | undefined }) {
  return (
    <span className="pg-bars" aria-label={v ? `Livello ${v} su 5` : 'Livello non ancora calcolato'}>
      {[1, 2, 3, 4, 5].map((n) => (
        <i key={n} className={v && n <= v ? 'on' : ''} />
      ))}
    </span>
  );
}

function Table({ head, rows, cols }: { head: string[]; rows: ReactNode[][]; cols?: string }) {
  if (!rows.length) return null;
  return (
    <div className="pg-table" style={cols ? ({ '--cols': cols } as React.CSSProperties) : undefined}>
      <div className="pg-tr pg-th">
        {head.map((h) => (
          <span key={h}>{h}</span>
        ))}
      </div>
      {rows.map((r, i) => (
        <div key={i} className="pg-tr">
          {r.map((c, k) => (
            <span key={k} data-l={head[k]}>
              {c}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function KV({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="pg-kv">
      {rows
        .filter(([, v]) => v !== null && v !== undefined && v !== '')
        .map(([k, v]) => (
          <span key={k} style={{ display: 'contents' }}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </span>
        ))}
    </dl>
  );
}

function Figures({ list }: { list: PhaseFigureDto[] | undefined }) {
  if (!list?.length) return null;
  return (
    <div className="pg-figs">
      {list.map((f) => (
        <div key={f.etichetta} className={`pg-fig ${f.da_confermare ? 'gold' : ''}`}>
          <span className="k">{f.etichetta}</span>
          <span className="v">{f.valore}</span>
          <span className="s">{f.fonte}</span>
        </div>
      ))}
    </div>
  );
}

/** Un testo dei documenti ufficiali scritto tutto in maiuscolo, in forma leggibile (la citazione resta nel «perché?»). */
const read = (s: string | null | undefined) => {
  if (!s || s !== s.toUpperCase() || !/[A-Z]{4}/.test(s)) return s ?? '';
  // Un nome breve resta con le iniziali maiuscole; un testo lungo (l'oggetto sociale) diventa una frase.
  if (s.length <= 60) return leggibile(s);
  const low = s.toLowerCase().replace(/\s+/g, ' ').trim();
  return low.charAt(0).toUpperCase() + low.slice(1);
};

/* ------------------------------------------------------------------ le fasi */

function P0({ d }: { d: PhaseZeroDto }) {
  return (
    <>
      <div className="pg-card">
        <h4>Il profilo</h4>
        <KV
          rows={[
            ['Settore', d.profilo.settore ?? 'da confermare'],
            ['Dimensione', d.profilo.dimensione ?? 'da confermare'],
            ['Territorio', d.profilo.territorio ?? 'da confermare'],
          ]}
        />
        {d.profilo.non_torna.length > 0 && <p className="pg-note gold">Non torna tra le fonti: {d.profilo.non_torna.join(' ')}</p>}
      </div>
      <div className="pg-grid">
        {d.sezioni.map((s) => (
          <div key={s.chiave} className="pg-card">
            <div className="pg-cardhead">
              <h4>{s.titolo}</h4>
              <Src fonte={d.fonte} grado={d.grado} titolo={s.titolo} testo={s.perche} />
            </div>
            <KV rows={s.voci.map((v) => [v.campo, <span key={v.campo} className={v.da_confermare ? 'gold' : ''}>{read(v.valore)}{v.da_confermare ? ' · da confermare' : ''}</span>])} />
            {s.contraddizione && <p className="pg-note gold">{s.contraddizione}</p>}
          </div>
        ))}
      </div>
      {d.conti.gradini.length > 0 && (
        <div className="pg-card">
          <h4>I conti, in tre gradini</h4>
          <Table head={['Gradino', 'Cosa', 'Stato']} cols="70px 1fr 140px" rows={d.conti.gradini.map((g) => [String(g.n), <span key="t"><b>{g.titolo}.</b> {g.testo}</span>, g.stato.replace('_', ' ')])} />
        </div>
      )}
    </>
  );
}

function P1({ d }: { d: PhaseOneDto }) {
  const vende = (['imprese', 'privati', 'pubblica_amministrazione'] as const).filter((k) => d.a_chi_vende[k]).map((k) => (k === 'pubblica_amministrazione' ? 'pubblica amministrazione' : k));
  return (
    <>
      <Figures list={d.numeri} />
      <div className="pg-grid">
        <div className="pg-card">
          <div className="pg-cardhead">
            <h4>La classe dimensionale</h4>
            <Src fonte="Raccomandazione 2003/361/CE" titolo="La classe dimensionale" testo={d.classe.perche} />
          </div>
          <p className="pg-big">{d.classe.vale ?? 'da confermare'}</p>
          {d.classe.manca && <p className="pg-note gold">Manca: {d.classe.manca}</p>}
          {d.classe.collegate.length > 0 && <Table head={['Impresa collegata', 'Legame', 'Fonte']} rows={d.classe.collegate.map((c) => [c.nome, c.legame, <Src key="f" fonte={c.fonte} titolo={c.nome} testo={c.dati} />])} />}
        </div>
        <div className="pg-card">
          <h4>A chi vende</h4>
          <p className="pg-big">{vende.join(', ') || 'da confermare'}</p>
          {d.a_chi_vende.dove && <p className="pg-note">{d.a_chi_vende.dove}</p>}
          {d.gruppo && (
            <>
              <h4>Il gruppo</h4>
              <p>{d.gruppo.capogruppo ? `${read(d.gruppo.capogruppo.nome)}${d.gruppo.quota ? `, ${d.gruppo.quota}` : ''}` : d.gruppo.nota}</p>
            </>
          )}
        </div>
      </div>
      <div className="pg-card">
        <h4>Cosa fa</h4>
        {d.cosa_fa.frasi.map((f, i) => (
          <p key={i} className="pg-sentence">
            {f.testo} {f.consulente && <span className="pg-tag vi">secondo il consulente</span>}
            {f.fonti.map((k) => d.cosa_fa.fonti[k]).filter(Boolean).map((c, j) => (
              <Src key={j} fonte={c!.testo} grado={c!.grado} titolo="Cosa fa" testo={c!.citazione ? `«${c!.citazione}»` : null} />
            ))}
          </p>
        ))}
      </div>
      {d.contratti.length > 0 && (
        <div className="pg-card">
          <h4>I contratti collettivi</h4>
          <Table head={['Contratto', 'A chi', 'Grado']} cols="1.6fr 1fr 150px" rows={d.contratti.map((c) => [<span key="n">{c.nome}{c.codice ? <span className="pg-mono"> · {c.codice}</span> : null}</span>, c.a_chi ?? '—', <span key="g" className={`pg-grade ${c.grado === 'T' ? 'certo' : 'probabile'}`}>{c.grado === 'T' ? 'risposta tua' : c.grado === 'scelta_timonio' ? 'scelta di Timonio' : 'probabile'}</span>])} />
        </div>
      )}
      {d.conti && (
        <div className="pg-card">
          <h4>I conti letti</h4>
          <p className="pg-note">{d.conti.gradino_testo}</p>
          <Table head={['Indice', 'Formula', 'Valore', 'Trend']} cols="1.2fr 1.4fr 120px 1fr" rows={d.conti.indici.map((x) => [x.nome, <span key="f" className="pg-mono">{x.formula}</span>, <b key="v" className={`pg-mono ${x.da_confermare ? 'gold' : ''}`}>{x.valore}</b>, x.trend])} />
          {d.conti.settore.length > 0 && <Table head={['Confronto con il settore', 'Azienda', 'Settore', 'Fonte']} rows={d.conti.settore.map((x) => [x.rapporto, <b key="a" className="pg-mono">{x.azienda}</b>, <span key="s" className="pg-mono">{x.settore}</span>, <Src key="f" fonte={x.fonte} titolo={x.rapporto} />])} />}
          {d.conti.numeri.map((n, i) => (
            <p key={i} className="pg-sentence">
              {n.testo} <Src fonte={n.fonte} titolo="Cosa dicono i numeri" />
            </p>
          ))}
        </div>
      )}
    </>
  );
}

function P2({ d }: { d: PhaseTwoDto }) {
  return (
    <>
      <Figures list={d.numeri} />
      <div className="pg-grid">
        {d.sedi.map((s) => (
          <div key={s.id} className="pg-card">
            <div className="pg-cardhead">
              <h4>{s.nome}</h4>
              <Grade g={s.grado} />
            </div>
            <KV
              rows={[
                ['Tipo e uso', [s.tipo, s.uso].filter(Boolean).join(' · ') || 'da confermare'],
                ['Indirizzo', <span key="i">{s.indirizzo ?? 'da confermare'} <Src fonte={s.indirizzo_fonte} titolo={`${s.nome}: indirizzo`} /></span>],
                ['Addetti', <span key="a">{s.addetti ?? 'da confermare'} <Src fonte={s.addetti_fonte} titolo={`${s.nome}: addetti`} /></span>],
                ['Attività', s.attivita],
                ['Insegna', s.insegna],
              ]}
            />
            {s.nota && <p className="pg-note gold">{s.nota}</p>}
            {s.attributi.length > 0 && (
              <Table head={['Peso', 'Attributo', 'Cosa risulta', 'Fonte']} cols="54px 1fr 1.3fr 1fr" rows={s.attributi.map((a) => [<span key="p" className="pg-mono">{a.peso}</span>, a.gruppo.replace(/_/g, ' '), <span key="v" className={a.esito === 'da_confermare' ? 'gold' : ''}>{a.valore}</span>, <Src key="f" fonte={a.fonte} grado={a.grado} titolo={`${s.nome}: ${a.gruppo.replace(/_/g, ' ')}`} />])} />
            )}
          </div>
        ))}
      </div>
      <p className="pg-note">Addetti senza sede: {d.addetti_senza_sede.numero} ({d.addetti_senza_sede.perche}). {d.altre_sedi}</p>
    </>
  );
}

function P3({ d }: { d: PhaseThreeDto }) {
  return (
    <>
      <Figures list={d.numeri} />
      <div className="pg-card">
        <Table
          head={['Attività', 'Codice', 'Dove si svolge', 'Per chi', 'Stato', 'Grado']}
          cols="1.6fr 90px 1.2fr 1fr 130px 100px"
          rows={d.attivita.map((a) => [
            <span key="n">
              <b>{a.nome_standard}</b>
              {a.nome_azienda && <span className="pg-sub">«{a.nome_azienda}»</span>}
              {a.prevalente && <span className="pg-tag cy">prevalente</span>}
            </span>,
            <span key="c" className="pg-mono">{a.codice ?? 'senza codice'}</span>,
            a.dove,
            a.per_chi.join(', ').replace('pubblica_amministrazione', 'pubblica amministrazione'),
            a.stato.replace('_', ' '),
            <Grade key="g" g={a.grado} />,
          ])}
        />
      </div>
      {d.servizi_online.length > 0 && (
        <div className="pg-card">
          <h4>I servizi online</h4>
          <Table head={['Servizio', 'Cosa fa', 'Fonte']} rows={d.servizi_online.map((s) => [<a key="u" href={s.indirizzo_web}>{s.testo}</a>, s.cosa_fa, <Src key="f" fonte={s.fonte} titolo={s.testo} />])} />
        </div>
      )}
    </>
  );
}

function P4({ d }: { d: PhaseFourDto }) {
  return (
    <>
      <Figures list={d.numeri} />
      {d.primari.map((p) => (
        <div key={p.id} className="pg-card">
          <div className="pg-cardhead">
            <h4>{p.nome}</h4>
            <span>
              <Grade g={p.grado} /> <Src fonte={p.fonti[0] ?? p.etichetta} titolo={p.nome} testo={p.descrizione} />
            </span>
          </div>
          <p className="pg-note">{p.descrizione}</p>
          <ol className="pg-steps">
            {p.fasi_del_processo.map((f) => (
              <li key={f.n}>
                <b>{f.nome}</b>
                <span className="pg-sub">
                  entra {f.entra} → esce {f.esce} · {f.dove}
                </span>
              </li>
            ))}
          </ol>
          <KV rows={[['Attività', p.attivita], ['Dove', p.dove], ['Responsabile', p.responsabile], ['Volume', p.volume]]} />
        </div>
      ))}
      {d.supporto.length > 0 && (
        <div className="pg-card">
          <h4>I processi di supporto</h4>
          <Table head={['Processo', 'Interno o affidato', 'Responsabile', 'Grado']} cols="1.5fr 1.2fr 1.2fr 100px" rows={d.supporto.map((p) => [<b key="n">{p.nome}</b>, p.gestione === 'affidato' ? `affidato a ${p.affidato_a ?? '…'}` : 'interno', p.responsabile, <Grade key="g" g={p.grado} />])} />
        </div>
      )}
    </>
  );
}

function P5({ d }: { d: PhaseFiveDto }) {
  return (
    <>
      <Figures list={d.numeri} />
      {d.sedi.map((s) => (
        <div key={s.id} className="pg-card">
          <div className="pg-cardhead">
            <h4>{s.nome}</h4>
            <span className="pg-sub">
              {s.titolo_uso} <Src fonte={s.titolo_uso_fonte} titolo={`${s.nome}: titolo d’uso`} />
            </span>
          </div>
          {s.gruppi.map((g) => (
            <div key={g.gruppo}>
              <h5>{PLANT_GROUP_TEXT[g.gruppo]}</h5>
              <Table head={['Impianto', 'Presenza', 'Di chi', 'Registro', 'Fonte']} cols="1.5fr 110px 120px 1.3fr 1fr" rows={g.impianti.map((x) => [<span key="n"><b>{x.nome}</b>{x.quantita ? ` · ${x.quantita}` : ''}</span>, <Grade key="p" g={x.presenza === 'presente' ? 'certo' : 'probabile'} />, x.di_chi === 'azienda' ? 'dell’azienda' : 'del proprietario', x.registro ?? '—', <Src key="f" fonte={x.fonte} titolo={x.nome} testo={x.descrizione} />])} />
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/** L'organigramma ad albero: ogni luogo con i suoi ruoli, i figli sotto. */
function OrgTree({ nodes }: { nodes: PhaseEightDto['organigramma'] }) {
  const kids = (id: string | null) => nodes.filter((n) => n.padre === id);
  const branch = (n: PhaseEightDto['organigramma'][number]): ReactNode => (
    <li key={n.id}>
      <div className={`pg-node ${n.luogo}`}>
        <span className="pg-mono">{n.luogo}</span>
        <b>{n.nome}</b>
        {n.ruoli && <span className="pg-sub">{n.ruoli}</span>}
        {n.persone != null && <span className="pg-mono">{n.persone} persone</span>}
      </div>
      {kids(n.id).length > 0 && <ul>{kids(n.id).map(branch)}</ul>}
    </li>
  );
  const roots = nodes.filter((n) => !n.padre || !nodes.some((x) => x.id === n.padre));
  return <ul className="pg-tree">{roots.map(branch)}</ul>;
}

function P8({ d }: { d: PhaseEightDto }) {
  return (
    <>
      <Figures list={d.numeri} />
      <div className="pg-card">
        <h4>Organigramma</h4>
        <OrgTree nodes={d.organigramma} />
      </div>
      {d.funzionigramma.length > 0 && (
        <div className="pg-card">
          <h4>Funzionigramma</h4>
          <Table head={['Funzione', 'Ruoli']} cols="1fr 2fr" rows={d.funzionigramma.map((f) => [<b key="f">{f.funzione}</b>, f.ruoli.join(', ')])} />
        </div>
      )}
      <div className="pg-grid">
        {d.ruoli.map((r) => (
          <div key={r.id} className="pg-card">
            <div className="pg-cardhead">
              <h4>{r.nome_uso}</h4>
              <span>
                <Grade g={r.grado} /> {r.secondo_il_consulente && <span className="pg-tag vi">secondo il consulente</span>}
              </span>
            </div>
            <p className="pg-sub">
              {r.nome_ufficiale}
              {r.codice_professione ? ` · ${r.codice_professione}` : ''}
            </p>
            <KV rows={[['Contratto', <span key="c">{r.contratto_esteso || r.contratto} <Src fonte={r.contratto_fonte} titolo={`${r.nome_uso}: contratto`} testo={r.contratto_nota} /></span>], ['Capo', r.capo], ['Dimensionamento', r.dimensionamento], ['Chi lo fa', r.chi_lo_fa]]} />
            {r.responsabilita.length > 0 && (
              <>
                <h5>Responsabilità</h5>
                <ul className="pg-list">
                  {r.responsabilita.map((x, i) => (
                    <li key={i}>
                      {x.testo} <Src fonte={x.fonte} titolo={r.nome_uso} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            {r.mansioni.length > 0 && (
              <>
                <h5>Mansioni</h5>
                <ul className="pg-list">
                  {r.mansioni.map((m, i) => (
                    <li key={i}>
                      <b>{m.nome}</b> {m.compiti.length ? <span className="pg-sub">{m.compiti.map((c) => c.testo).join('; ')}</span> : null} <Src fonte={m.fonte} titolo={m.nome} />
                    </li>
                  ))}
                </ul>
              </>
            )}
            {r.competenze.length > 0 && <p className="pg-chips">{r.competenze.map((c) => <span key={c} className="pg-tag">{c}</span>)}</p>}
          </div>
        ))}
      </div>
    </>
  );
}

function P9({ d }: { d: PhaseNineDto }) {
  return (
    <>
      <Figures list={d.numeri} />
      {d.procedure.map((p) => (
        <div key={p.id} className="pg-card">
          <div className="pg-cardhead">
            <h4>{p.nome}</h4>
            <span>
              <Grade g={p.grado} /> <Src fonte={p.norma_ente ?? p.appartiene_breve} titolo={p.nome} testo={p.perche ?? p.scopo} />
            </span>
          </div>
          <KV rows={[['Scopo', p.scopo], ['Appartiene a', p.appartiene_a], ['Chi la svolge', p.chi_svolge], ['Chi ne risponde', p.chi_risponde], ['Avvio', p.avvio]]} />
          <ol className="pg-steps">
            {p.passaggi.map((s) => (
              <li key={s.ordine}>
                {s.azione} <span className="pg-sub">· {s.ruolo}</span>
              </li>
            ))}
          </ol>
          <KV rows={[['Controlli', p.controlli.join('; ')], ['Traccia', p.traccia], ['Programmi', p.programmi.join(', ')], ['Indicatore', p.indicatore]]} />
        </div>
      ))}
    </>
  );
}

/* ------------------------------------------------------------------ la pagina */

/** Le sezioni finali del documento, dalla composizione dello studio (fonti, titolare, scelte di Timonio, domande aperte). */
function Blocks({ blocks }: { blocks: DocumentBlockDto[] }) {
  return (
    <>
      {blocks.map((b, i) =>
        b.tipo === 'testo' ? (
          <p key={i} className="pg-note">
            {b.testo}
          </p>
        ) : b.tipo === 'tabella' ? (
          <div key={i} className="pg-card">
            <Table head={b.intestazione.map((h, k) => h || `·${k}`)} rows={b.righe} />
          </div>
        ) : (
          <div key={i} className="pg-card">
            <Table head={['Voce', 'Fonte']} rows={b.voci.map((v) => [<span key="n"><b>{v.nome}</b>{v.testo ? <span className="pg-sub">{v.testo}</span> : null}</span>, <Src key="f" fonte={v.fonte} grado={v.grado} titolo={v.nome} />])} />
          </div>
        ),
      )}
    </>
  );
}

const FASI: Array<{ n: number; titolo: string }> = PHASES.map((p) => ({ n: p.n, titolo: p.titolo }));
const FINALI: Array<{ chiave: 'fonti' | 'titolare' | 'scelte' | 'allegato'; titolo: string }> = [
  { chiave: 'fonti', titolo: 'Le fonti' },
  { chiave: 'titolare', titolo: 'Cosa ci ha detto il titolare' },
  { chiave: 'scelte', titolo: 'Scelte di Timonio e domande aperte' },
  // L'allegato «Il dettaglio delle proposte» (fase 11, consegna): in fondo, dopo le scelte e le domande aperte.
  { chiave: 'allegato', titolo: 'Allegato · Il dettaglio delle proposte' },
];

/** Il documento scaricabile a tutta pagina: si apre dalla fase 11 e si chiude tornando al percorso. */
export function Progetto({ onClose }: { onClose: () => void }) {
  const doc = useTenantQuery<ProjectDocumentDto>(['consegna', 'documento'], '/onboarding/consegna/documento');
  const f0 = useTenantQuery<PhaseZeroDto>(['fase0'], '/onboarding/fase0');
  const pages = [
    useTenantQuery<PhaseOneDto>(['fase', 1], '/onboarding/fase/1'),
    useTenantQuery<PhaseTwoDto>(['fase', 2], '/onboarding/fase/2'),
    useTenantQuery<PhaseThreeDto>(['fase', 3], '/onboarding/fase/3'),
    useTenantQuery<PhaseFourDto>(['fase', 4], '/onboarding/fase/4'),
    useTenantQuery<PhaseFiveDto>(['fase', 5], '/onboarding/fase/5'),
    useTenantQuery<PhaseSixDto>(['fase', 6], '/onboarding/fase/6'),
    useTenantQuery<PhaseSevenDto>(['fase', 7], '/onboarding/fase/7'),
    useTenantQuery<PhaseEightDto>(['fase', 8], '/onboarding/fase/8'),
    useTenantQuery<PhaseNineDto>(['fase', 9], '/onboarding/fase/9'),
    useTenantQuery<PhaseTenDto>(['fase', 10], '/onboarding/fase/10'),
    useTenantQuery<PhaseElevenDto>(['fase', 11], '/onboarding/fase/11'),
  ];
  const data: Array<PhaseCommonDto | PhaseZeroDto | undefined> = [f0.data, ...pages.map((p) => p.data as PhaseCommonDto | undefined)];
  const [why, setWhy] = useState<Why | null>(null);
  const [current, setCurrent] = useState('copertina');
  const [menu, setMenu] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  // La fase corrente nell'indice mentre si scorre: l'ultima sezione la cui testa è arrivata in cima (anche quando le
  // sezioni sono corte e se ne vedono più d'una insieme).
  useEffect(() => {
    const root = scroller.current;
    if (!root) return;
    const onScroll = () => {
      const limit = root.getBoundingClientRect().top + 24;
      let id = 'copertina';
      root.querySelectorAll<HTMLElement>('section[data-sec]').forEach((s) => {
        if (s.getBoundingClientRect().top <= limit) id = s.dataset.sec!;
      });
      setCurrent(id);
    };
    root.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => root.removeEventListener('scroll', onScroll);
  }, []);

  // Lo scorrimento si calcola dentro il contenitore del documento: sotto resta montata la pagina della fase, con i suoi id.
  const go = (key: string) => {
    const root = scroller.current;
    const el = root?.querySelector<HTMLElement>(`section[data-sec="${key}"]`);
    if (root && el) root.scrollTo({ top: root.scrollTop + el.getBoundingClientRect().top - root.getBoundingClientRect().top, behavior: 'auto' });
    setCurrent(key);
    setMenu(false);
  };
  // Nessun id del documento si ripete nella pagina che resta sotto (art. 4.2: ogni voce dell'indice porta alla sua sezione).
  useEffect(() => {
    const seen = new Map<string, number>();
    document.querySelectorAll('[id]').forEach((e) => seen.set(e.id, (seen.get(e.id) ?? 0) + 1));
    const twice = [...scroller.current?.querySelectorAll('[id]') ?? []].map((e) => e.id).filter((id) => (seen.get(id) ?? 0) > 1);
    if (twice.length) console.error(`Il documento ha id ripetuti nella pagina: ${[...new Set(twice)].join(', ')}`);
  });

  const level = (x: PhaseCommonDto | PhaseZeroDto | undefined) => x?.livello?.valore ?? null;
  const azienda = doc.data?.azienda ?? 'L’azienda';
  const finali = useMemo(() => new Map((doc.data?.sezioni ?? []).map((s) => [s.chiave, s])), [doc.data]);

  return createPortal(
    <WhyCtx.Provider value={setWhy}>
      <div className="sn pg">
        <div className="pg-bar noprint">
          <button type="button" className="pg-menu" onClick={() => setMenu((m) => !m)} aria-expanded={menu}>
            Indice
          </button>
          <span className="pg-brand">Timonio · il progetto di {azienda}</span>
          <span className="pg-actions">
            <button type="button" className="sn-btn sm" onClick={() => window.print()}>
              Salva in PDF
            </button>
            <button type="button" className="sn-btn ghost sm" onClick={onClose}>
              Chiudi
            </button>
          </span>
        </div>
        <nav className={`pg-nav ${menu ? 'open' : ''}`} aria-label="Indice delle fasi">
          <p className="pg-navtitle">Indice</p>
          <a className={current === 'copertina' ? 'on' : ''} href="#prog-copertina" onClick={(e) => (e.preventDefault(), go('copertina'))}>
            <span className="n">·</span>
            <span>Copertina e nota</span>
          </a>
          {FASI.map((f) => (
            <a key={f.n} className={current === `f${f.n}` ? 'on' : ''} href={`#prog-f${f.n}`} onClick={(e) => (e.preventDefault(), go(`f${f.n}`))}>
              <span className="n">{f.n}</span>
              <span className="t">
                {f.titolo}
                <Bars v={level(data[f.n])} />
              </span>
            </a>
          ))}
          {FINALI.map((f) => (
            <a key={f.chiave} className={current === f.chiave ? 'on' : ''} href={`#prog-${f.chiave}`} onClick={(e) => (e.preventDefault(), go(f.chiave))}>
              <span className="n">·</span>
              <span>{f.titolo}</span>
            </a>
          ))}
        </nav>
        <div className="pg-main" ref={scroller}>
          <section id="prog-copertina" data-sec="copertina" className="pg-cover">
            <p className="pg-eyebrow">Il progetto dell’azienda al suo meglio</p>
            <h1>{azienda}</h1>
            <p className="pg-meta">
              {doc.data?.partita_iva ? `Partita IVA ${doc.data.partita_iva} · ` : ''}documento del {new Date(doc.data?.il ?? Date.now()).toLocaleDateString('it-IT')}
            </p>
            <Constellation levels={data.map(level)} onGo={go} />
            <p className="pg-tutela">{doc.data?.nota ?? 'Carico la nota di tutela…'}</p>
            <ol className="pg-printindex">
              {FASI.map((f) => (
                <li key={f.n}>
                  <span>{f.n}</span> {f.titolo} <i>livello {level(data[f.n]) ?? '—'} su 5</i>
                </li>
              ))}
            </ol>
          </section>
          {FASI.map((f) => (
            <PhaseSection key={f.n} n={f.n} titolo={f.titolo} d={data[f.n]} error={f.n === 0 ? f0.error : pages[f.n - 1]!.error} />
          ))}
          {FINALI.map((f) => (
            <section key={f.chiave} id={`prog-${f.chiave}`} data-sec={f.chiave} className="pg-phase">
              <header className="pg-head">
                <h2>{f.titolo}</h2>
              </header>
              {f.chiave === 'allegato' ? (
                (data[11] as PhaseElevenDto | undefined)?.proposte?.length ? (
                  <div className="snc">
                    <Proposals d={data[11] as PhaseElevenDto} tutte kpi={false} />
                  </div>
                ) : (
                  <p className="pg-note">Le proposte non sono ancora pronte.</p>
                )
              ) : doc.data ? (
                <Blocks blocks={finali.get(f.chiave)?.blocchi ?? []} />
              ) : (
                <p className="pg-note">{doc.error ? errorMessage(doc.error) : 'Preparo…'}</p>
              )}
            </section>
          ))}
        </div>
        {why && (
          <aside className="pg-why noprint" role="dialog" aria-label="Perché?">
            <button type="button" className="pg-close" onClick={() => setWhy(null)} aria-label="Chiudi">
              ×
            </button>
            <p className="pg-eyebrow">Perché?</p>
            <h3>{why.titolo}</h3>
            {why.fonte && (
              <p>
                <b>Fonte.</b> {why.fonte}
                {why.grado && <span className={`pg-src g-${why.grado.toLowerCase()}`} style={{ marginLeft: 8 }}><b>{why.grado}</b></span>}
              </p>
            )}
            {why.grado && <p className="pg-note">{GRADE_TEXT[why.grado] ?? ''}</p>}
            {why.testo && <p>{why.testo}</p>}
          </aside>
        )}
      </div>
    </WhyCtx.Provider>,
    document.body,
  );
}

const GRADE_TEXT: Record<string, string> = {
  A: 'Grado A: un registro o un documento ufficiale (visura, bilancio depositato, norma vigente).',
  B: 'Grado B: il sito dell’azienda.',
  C: 'Grado C: una pagina pubblica che riporta la partita IVA.',
  D: 'Grado D: una stima o una fonte indiretta.',
  T: 'Grado T: detto dal titolare, con la data.',
};

/** I 12 livelli come costellazione: una stella per fase, più luminosa più il livello è alto; un clic porta alla fase. */
function Constellation({ levels, onGo }: { levels: Array<number | null>; onGo: (id: string) => void }) {
  const pts = levels.map((v, i) => ({ v, x: 30 + i * 52, y: 70 + Math.sin(i * 1.3) * 34 }));
  return (
    <svg className="pg-stars" viewBox="0 0 640 150" role="img" aria-label="I livelli delle 12 fasi">
      <polyline points={pts.map((p) => `${p.x},${p.y}`).join(' ')} />
      {pts.map((p, i) => (
        <g key={i} onClick={() => onGo(`f${i}`)} style={{ cursor: 'pointer' }}>
          <circle cx={p.x} cy={p.y} r={4 + (p.v ?? 0) * 1.6} className={`l${p.v ?? 0}`} />
          <text x={p.x} y={p.y + 30} textAnchor="middle">
            {i}
          </text>
          <title>{`Fase ${i}: livello ${p.v ?? '—'} su 5`}</title>
        </g>
      ))}
    </svg>
  );
}

function PhaseSection({ n, titolo, d, error }: { n: number; titolo: string; d: PhaseCommonDto | PhaseZeroDto | undefined; error: unknown }) {
  const done = d?.tempi?.fine ? clock(spent(d.tempi.conteggio_da ?? d.tempi.inizio, d.tempi.fine, 0)) : null;
  const ready = d && d.stato === 'pronta';
  return (
    <section id={`prog-f${n}`} data-sec={`f${n}`} className="pg-phase">
      <header className="pg-head">
        <span className="pg-num">{n}</span>
        <div>
          <h2>{titolo}</h2>
          <p className="pg-sub">
            <Bars v={d?.livello?.valore} /> {d?.livello ? `Livello ${d.livello.valore} su 5, ${d.livello.nome}` : 'Livello non ancora calcolato'}
            {done ? ` · fatta in ${done}` : ''}
          </p>
        </div>
      </header>
      {!d ? (
        <p className="pg-note">{error ? errorMessage(error) : 'Carico la fase…'}</p>
      ) : !ready ? (
        <p className="pg-note">La fase non è ancora pronta: la sezione si riempie quando lo è.</p>
      ) : (
        <PhaseBody n={n} d={d} />
      )}
    </section>
  );
}

function PhaseBody({ n, d }: { n: number; d: PhaseCommonDto | PhaseZeroDto }) {
  switch (n) {
    case 0:
      return <P0 d={d as PhaseZeroDto} />;
    case 1:
      return <P1 d={d as PhaseOneDto} />;
    case 2:
      return <P2 d={d as PhaseTwoDto} />;
    case 3:
      return <P3 d={d as PhaseThreeDto} />;
    case 4:
      return <P4 d={d as PhaseFourDto} />;
    case 5:
      return <P5 d={d as PhaseFiveDto} />;
    case 6:
      return <Fase6Body d={d as PhaseSixDto} />;
    case 7:
      return <Fase7Body d={d as PhaseSevenDto} />;
    case 8:
      return <P8 d={d as PhaseEightDto} />;
    case 9:
      return <P9 d={d as PhaseNineDto} />;
    case 10:
      return (
        <div className="snc">
          <Diagnosis d={d as PhaseTenDto} />
        </div>
      );
    case 11:
      return (
        <div className="snc">
          <ProposalsSummary d={d as PhaseElevenDto} />
          <p className="note">Il dettaglio di ogni proposta, il percorso e la mappa per processo e per ruolo sono nell’allegato, in fondo al documento.</p>
        </div>
      );
    default:
      return null;
  }
}
